/**
 * Created by calhosh on 1/22/2025
 */
import _ from 'lodash';
import React, {useCallback, useEffect, useState, useRef} from 'react';
import {useUserPreferences} from '../../../util/customHooks';
import {useAresUrlParameters} from '../../../util/Ares/customHooks';
import config from '@ey/voltron-config';
import {EngagementStatus, hideAIChatbotForEngagementSources} from '../../../util/uiconstants';
import {useDispatch, useSelector} from 'react-redux';
import {getAIChatBotIntegrationParameters} from '../../../actions/AIChatbot/AIChatbotActions';
import RemoteComponent from './RemoteComponent';
import styled from 'styled-components';
import {getTaskDocumentByDocumentId} from '../../../selectors/Ares/selectors';

export default function AIChatbot(props) {
	const {show, setShow} = props;
	const {documentId} = useAresUrlParameters();
	const context = useSelector((state) => state.aiChatBotIntegrationParameters);
	const drawerFromDetails = useSelector((state) => state.formDetailsOpenedInDrawer);
	const configurations = useSelector((store) => store.configurations);
	const engagement = useSelector((store) => store.engagement);
	const user = useUserPreferences();
	const dispatch = useDispatch();
	const formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const [isLoading, setIsLoading] = useState(true);

	const getFormData = () => {
		if (
			drawerFromDetails &&
			!drawerFromDetails.isDrawerCollapsed &&
			Array.isArray(drawerFromDetails.allIds) &&
			drawerFromDetails.allIds.length > 0
		) {
			const activeFrame = drawerFromDetails.allIds
				.map((id) => drawerFromDetails.byId[id])
				.find((formDetail) => formDetail?.isActiveFrame);

			if (activeFrame) {
				const activeFrameHeader = Array.isArray(activeFrame?.formData?.headers)
					? activeFrame.formData.headers.find((header) => header.id === activeFrame.headerId)
					: undefined;

				const activeFrameSection = Array.isArray(activeFrameHeader?.sections)
					? activeFrameHeader.sections.find((section) => section.id === activeFrame.sectionId)
					: undefined;

				return {
					formData: activeFrame.formData,
					header: activeFrameHeader,
					section: activeFrameSection,
					pageDetails: {
						pageUrl: activeFrame.deepLinkSrc,
						knowledgeTaskId: 0
					}
				};
			}
		}

		const header = Array.isArray(formData?.headers)
			? formData.headers.find((header) => header.id === context.headerId)
			: undefined;

		const section = Array.isArray(header?.sections)
			? header.sections.find((section) => section.id === context.sectionId)
			: undefined;

		return {
			formData,
			header,
			section,
			pageDetails: {
				pageUrl: window.location.href,
				knowledgeTaskId: 0
			}
		};
	};

	const getChatBotPayload = useCallback(() => {
		if (context?.data !== undefined) {
			const data = getFormData();
			const formDetails = {
				knowledgeFormId: data?.formData?.knowledgeFormId ?? null,
				knowledgeHeaderId: data?.header?.knowledgeFormHeaderId ?? null,
				KnowledgeSectionId: data?.section?.knowledgeFormSectionId ?? null,
				formLocationCode: data?.formData?.guidanceAndGamLayer?.countryCode ?? null,
				formLanguageCode: data?.formData?.guidanceAndGamLayer?.languageCode ?? null,
				gamLayerComputedValue: data?.formData?.guidanceAndGamLayer?.gamLayerComputedValue ?? null,
				gamLayer: data?.formData?.guidanceAndGamLayer?.gamLayers ?? null
			};

			const isOrchestratedContextRestricted = config.aiChatBotConfiguration.IsOrchestratedContextRestricted;
			
			const engagementDetails = {
				...context.data.engagementDetails,
				engagementName:
					isOrchestratedContextRestricted === 'true' ? '' : context.data.engagementDetails.engagementName,
				engagementCodes:
					isOrchestratedContextRestricted === 'true' ? '' : context.data.engagementDetails.engagementCodes,
				startDate: context.data.engagementDetails.startDate?.split('T')[0],
				endDate: context.data.engagementDetails.endDate?.split('T')[0],
				clientYearEndDate: context.data.engagementDetails.clientYearEndDate?.split('T')[0],
				engagementPeriodEndDate: context.data.engagementDetails.engagementPeriodEndDate?.split('T')[0],
				expectedReportReleaseDate: context.data.engagementDetails.expectedReportReleaseDate?.split('T')[0],
				archivalDeadlineDate: context.data.engagementDetails.archivalDeadlineDate?.split('T')[0]
			};


			const engagementProfiles =
				context.data.entities?.[0]?.engagementProfiles?.length > 0
					? Object.assign({}, ...context.data.entities[0].engagementProfiles)
					: {};

			return {
				pageDetails: data?.pageDetails,
				engagementDetails,
				gamLayer: context.data.entities?.[0]?.gamLayer,
				engagementProfiles,
				formDetails
			};
		}
	}, [context, formData, drawerFromDetails]);

	useEffect(() => {
		if (isAIChatBotEnabled()) {
			dispatch(getAIChatBotIntegrationParameters(Number(engagement.engagementId))).then(() => {
				setIsLoading(false);
			});
		}
	}, []);

	const toggleOrchestrator = () => {
		setShow(!show);
	};

	const isAIChatBotEnabled = () =>
		configurations.features?.isOrchestratorEnabled &&
		user != null &&
		!user?.isGuestUser &&
		engagement?.engagementStatusId !== undefined &&
		engagement.engagementStatusId !== EngagementStatus.Archived &&
		!hideAIChatbotForEngagementSources.includes(engagement.engagementSourceId);

	const OrchestratorProps = {
		onClose: toggleOrchestrator,
		envConfig: {
			// Need to check either need to put in config or will put in utility
			// host's own mechanism should be used
			HEARTBEAT_ENABLED: config.aiChatBotConfiguration.HEARTBEAT_ENABLED || 'false',
			// host's own policy should include a section
			PRIVACY_POLICY_ENABLED: config.aiChatBotConfiguration.PRIVACY_POLICY_ENABLED || 'true',
			// host's own header should cover this
			AVATAR_BUTTON_ENABLED: config.aiChatBotConfiguration.AVATAR_BUTTON_ENABLED || 'false',
			// no profiles needed, context comes dynamically
			PROFILE_SELECTOR_ENABLED: config.aiChatBotConfiguration.PROFILE_SELECTOR_ENABLED || 'false',
			// no skill selection should be allowed
			PLUGIN_OVERRIDE_TOGGLE_ENABLED: config.aiChatBotConfiguration.PLUGIN_OVERRIDE_TOGGLE_ENABLED || 'false',
			// skill usage should be limited to Atlas
			PLUGIN_OVERRIDE_DEFAULT_VALUE: config.aiChatBotConfiguration.PLUGIN_OVERRIDE_DEFAULT_VALUE || 'AtlasPlugin',
			// disclaimer should be hidden
			WARNING_PANEL_ENABLED: config.aiChatBotConfiguration.WARNING_PANEL_ENABLED || 'false',
			// no profiles needed, see above
			PROFILE_PANE_ENABLED: config.aiChatBotConfiguration.PROFILE_PANE_ENABLED || 'false',
			// no additional providers needed
			CHAT_PROVIDER_PANE_ENABLED: config.aiChatBotConfiguration.CHAT_PROVIDER_PANE_ENABLED || 'false',
			// no settings panel needed
			SETTINGS_BUTTON_ENABLED: config.aiChatBotConfiguration.SETTINGS_BUTTON_ENABLED || 'false',
			// close button enable
			CLOSE_BUTTON_ENABLED: config.aiChatBotConfiguration.CLOSE_BUTTON_ENABLED || 'true'
		},
		// fixed value - required for video insights
		dynamicInsightsGroup: 'canvas',
		// preferably set this in a dynamic way - required for video insights

		style: {position: 'fixed', right: 0, height: 'calc(100vh - var(--px-56))', zIndex: 999},

		onBeforePostMessage: (payload) => {
			payload.context = getChatBotPayload();
		},

		adConfig: {
			auth: {clientId: config.ad.auth.clientId}
		},

		dynamicInsightsKey: JSON.stringify(getChatBotPayload())
	};

	return (
		<StyledAIChatbot>
			{isAIChatBotEnabled() && !isLoading && context?.data !== undefined ? (
				<RemoteComponent className={`${!show ? 'hidden' : ''}`} id="orchestrator" {...OrchestratorProps} />
			) : (
				<></>
			)}
		</StyledAIChatbot>
	);
}

const StyledAIChatbot = styled.div`
	.hidden {
		display: none;
	}
`;
